import { useCallback, useEffect, useMemo, useState } from 'react';
import { PackageCheck } from 'lucide-react';
import {
  describeError,
  describeFieldError,
  getAgentReleases,
  problemErrorCode,
  problemStatus,
  updateAgentReleasePolicy,
  type AgentReleaseChannel,
  type AgentReleasesResponse,
} from '@/lib/api';
import { compareAgentVersions, countLevels, selectableVersions, type LevelCounts } from '@/lib/agent-version';
import { channelLabel, channelOf, formatWhen, operatorLabel } from '@/lib/printing';
import { useAuthStore } from '@/stores/auth';
import { Badge } from './ui';
import { CardTable, THead, TBody, Tr, Th, Td } from './CardTable';
import { SimpleConfirmDialog } from './SimpleConfirmDialog';

/**
 * Seção "Versões do agente" da página "Impressão" (change print-agent, tarefa 8.2).
 *
 * Separada das contagens da frota de propósito: a frota responde "a impressão está
 * saindo?", e esta seção responde "que versão do agente o Vapt Menu exige de cada canal?". Para
 * cada canal (piloto e estável) mostra a mínima e a recomendada, quem alterou e quando, as
 * versões publicadas no manifesto do feed, com a data e a assinatura, e quantos computadores
 * ativos das lojas do canal estão em cada versão e em cada protocolo.
 *
 * O pipeline só publica versões; quem escolhe a mínima e a recomendada é um `Admin`, aqui,
 * entre as versões do manifesto que a API aceita para o canal. A escolha passa por uma
 * prévia do impacto (quantos computadores passam a ver o diálogo obrigatório e o aviso) e
 * por confirmação. Conflito de edição recarrega os valores atuais sem apagar a escolha, e
 * com o feed do canal indisponível a edição fica desabilitada.
 */

interface Policy {
  minimumVersion: string | null;
  recommendedVersion: string | null;
}

const intFmt = new Intl.NumberFormat('pt-BR');

function computers(count: number): string {
  return count === 1 ? '1 computador' : `${intFmt.format(count)} computadores`;
}

export function AgentReleasesSection({ reloadKey = 0 }: { reloadKey?: number }) {
  const isAdmin = useAuthStore((s) => s.operator?.role) === 'Admin';
  const currentOperatorId = useAuthStore((s) => s.operator?.id ?? null);

  const [data, setData] = useState<AgentReleasesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localReload, setLocalReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getAgentReleases()
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch((err) => !cancelled && setError(describeError(err)));
    return () => {
      cancelled = true;
    };
  }, [reloadKey, localReload]);

  const reload = useCallback(() => setLocalReload((n) => n + 1), []);

  const replaceChannel = useCallback((updated: AgentReleaseChannel) => {
    setData((prev) =>
      prev && {
        ...prev,
        channels: prev.channels.map((c) => (c.channel === updated.channel ? updated : c)),
      },
    );
  }, []);

  return (
    <section className="card" aria-labelledby="agent-releases-title">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <PackageCheck size={18} />
        <h2 className="card-title" id="agent-releases-title" style={{ margin: 0 }}>
          Versões do agente
        </h2>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        A mínima e a recomendada de cada canal decidem o que o dono vê no painel: abaixo da
        recomendada, um aviso que ele pode dispensar; abaixo da mínima, um diálogo que não fecha
        sem escolher quando atualizar. Publicar uma versão no feed não muda nenhuma das duas.
      </p>

      {error && (
        <div className="error-banner" role="alert">
          {error}
        </div>
      )}
      {!data && !error && <p className="muted">Carregando…</p>}

      {data && (
        <div style={{ display: 'grid', gap: 20 }}>
          <p style={{ margin: 0 }} data-testid="agent-protocol">
            Protocolo: a API fala o <strong>{data.protocol.current}</strong> e aceita a partir do{' '}
            <strong>{data.protocol.minimum}</strong>. Computador abaixo da mínima só consegue se
            atualizar.
          </p>
          {data.channels.map((release) => (
            <ChannelRelease
              key={release.channel}
              release={release}
              minimumProtocol={data.protocol.minimum}
              isAdmin={isAdmin}
              currentOperatorId={currentOperatorId}
              onSaved={replaceChannel}
              onReload={reload}
            />
          ))}
        </div>
      )}
    </section>
  );
}

type Notice = { tone: 'success' | 'warning' | 'error'; text: string };

function ChannelRelease({
  release,
  minimumProtocol,
  isAdmin,
  currentOperatorId,
  onSaved,
  onReload,
}: {
  release: AgentReleaseChannel;
  minimumProtocol: number;
  isAdmin: boolean;
  currentOperatorId: string | null;
  onSaved: (updated: AgentReleaseChannel) => void;
  onReload: () => void;
}) {
  const label = channelLabel(release.channel);
  const current: Policy = {
    minimumVersion: release.minimumVersion,
    recommendedVersion: release.recommendedVersion,
  };

  // A escolha do administrador vive aqui, e não nos dados: recarregar a política depois de
  // um 409 troca os valores atuais sem apagar o que ele tinha escolhido.
  const [draft, setDraft] = useState<Policy | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const feedOk = release.feed.state === 'ok';
  const choice = draft ?? current;
  const dirty =
    draft !== null &&
    (draft.minimumVersion !== current.minimumVersion || draft.recommendedVersion !== current.recommendedVersion);

  const options = useMemo(() => selectableVersions(release), [release]);
  const optionSet = useMemo(() => new Set(options.map((v) => v.version)), [options]);

  // A API também recusa (400 no campo da mínima); aqui o botão nem habilita.
  const orderError =
    (compareAgentVersions(choice.minimumVersion, choice.recommendedVersion) ?? 0) > 0
      ? 'A mínima não pode ser maior que a recomendada.'
      : null;

  // A prévia conta como a API: pela versão e, para os de protocolo recusado, pela versão-alvo.
  const before = useMemo(
    () =>
      countLevels(
        release.agentVersions,
        release.minimumVersion,
        release.recommendedVersion,
        release.refusedProtocolAgentVersions,
      ),
    [release.agentVersions, release.refusedProtocolAgentVersions, release.minimumVersion, release.recommendedVersion],
  );
  const after = useMemo(
    () =>
      countLevels(
        release.agentVersions,
        choice.minimumVersion,
        choice.recommendedVersion,
        release.refusedProtocolAgentVersions,
      ),
    [release.agentVersions, release.refusedProtocolAgentVersions, choice.minimumVersion, choice.recommendedVersion],
  );

  function change(field: keyof Policy, value: string) {
    setNotice((n) => (n?.tone === 'success' ? null : n));
    setDraft({ ...choice, [field]: value === '' ? null : value });
  }

  async function save() {
    setSaving(true);
    try {
      const updated = await updateAgentReleasePolicy(release.channel, {
        minimumVersion: choice.minimumVersion,
        recommendedVersion: choice.recommendedVersion,
        concurrencyToken: release.concurrencyToken,
      });
      onSaved(updated);
      setDraft(null);
      setNotice({ tone: 'success', text: `Versões ${channelOf(release.channel)} salvas.` });
    } catch (err) {
      const status = problemStatus(err);
      if (status === 409) {
        setNotice({
          tone: 'warning',
          text:
            `Outro administrador alterou as versões ${channelOf(release.channel)} enquanto você escolhia. ` +
            'Os valores atuais foram recarregados e a sua escolha continua na tela: confira a prévia e salve de novo.',
        });
        onReload();
      } else if (status === 503 || problemErrorCode(err) === 'printing.agent_feed_unavailable') {
        setNotice({
          tone: 'error',
          text:
            'O feed de versões não pôde ser lido para conferir a escolha, e nada mudou. ' +
            'A edição fica desabilitada até o feed voltar; os computadores seguem funcionando.',
        });
        onReload();
      } else {
        setNotice({ tone: 'error', text: describeFieldError(err) });
      }
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  }

  const versionOptions = (value: string | null) => {
    // A versão em vigor pode ter saído das opções (manifesto mudou): continua visível no
    // campo, marcada, e a API recusa gravá-la de novo.
    const stale = value !== null && !optionSet.has(value);
    return (
      <>
        {options.map((v) => (
          <option key={v.version} value={v.version}>
            {v.version}
            {v.signed ? '' : ' (sem assinatura)'}
          </option>
        ))}
        {stale && (
          <option value={value}>
            {value} (fora das opções do canal)
          </option>
        )}
      </>
    );
  };

  return (
    <div
      role="group"
      aria-labelledby={`release-${release.channel}-title`}
      style={{ borderTop: '1px solid var(--color-border)', paddingTop: 16 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        <h3 id={`release-${release.channel}-title`} className="card-title" style={{ margin: 0 }}>
          {label}
        </h3>
        <FeedBadge release={release} />
      </div>

      {release.feed.state === 'unavailable' && (
        <div className="warning-banner" style={{ marginBottom: 12 }}>
          <strong>Feed do canal indisponível.</strong> A última leitura do manifesto de versões
          falhou{release.versions.length > 0 ? '; as versões abaixo são as da última leitura boa' : ''}.
          Mínima e recomendada não podem ser alteradas até o feed voltar. Os computadores seguem
          imprimindo.
        </div>
      )}
      {release.feed.state === 'not_configured' && (
        <div className="info-banner" style={{ marginBottom: 12 }}>
          Este ambiente não tem feed de versões configurado: não há versão para escolher.
        </div>
      )}

      <div className="detail-grid" style={{ marginBottom: 12 }}>
        <div className="k">Mínima</div>
        <div data-testid={`release-${release.channel}-minimum`}>
          {release.minimumVersion ? <span className="mono">{release.minimumVersion}</span> : 'Sem mínima'}
        </div>
        <div className="k">Recomendada</div>
        <div data-testid={`release-${release.channel}-recommended`}>
          {release.recommendedVersion ? <span className="mono">{release.recommendedVersion}</span> : 'Sem recomendada'}
        </div>
        <div className="k">Alterada</div>
        <div data-testid={`release-${release.channel}-updated`}>
          {release.updatedByOperatorId
            ? `por ${operatorLabel(release.updatedByOperatorId, currentOperatorId)} em ${formatWhen(release.updatedAt)}`
            : 'nunca alterada'}
        </div>
        <div className="k">Hoje no canal</div>
        <div>
          {computers(before.mandatory)} com atualização obrigatória · {computers(before.recommended)} com
          atualização recomendada
        </div>
      </div>

      <VersionsTable release={release} options={optionSet} />
      <FleetLists release={release} minimumProtocol={minimumProtocol} />

      {isAdmin && (
        <div style={{ marginTop: 16 }}>
          <div className="grid-2">
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor={`release-${release.channel}-min`}>Mínima {channelOf(release.channel)}</label>
              <select
                id={`release-${release.channel}-min`}
                value={choice.minimumVersion ?? ''}
                disabled={!feedOk || saving}
                onChange={(e) => change('minimumVersion', e.target.value)}
              >
                <option value="">Sem mínima</option>
                {versionOptions(choice.minimumVersion)}
              </select>
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label htmlFor={`release-${release.channel}-rec`}>Recomendada {channelOf(release.channel)}</label>
              <select
                id={`release-${release.channel}-rec`}
                value={choice.recommendedVersion ?? ''}
                disabled={!feedOk || saving}
                onChange={(e) => change('recommendedVersion', e.target.value)}
              >
                <option value="">Sem recomendada</option>
                {versionOptions(choice.recommendedVersion)}
              </select>
            </div>
          </div>

          {!feedOk && (
            <p className="muted" style={{ marginBottom: 0 }}>
              Edição desabilitada: sem leitura do feed, a API não confere as versões.
            </p>
          )}
          {feedOk && dirty && orderError && (
            <p className="muted" role="status" style={{ marginBottom: 0 }}>
              {orderError}
            </p>
          )}
          {feedOk && dirty && !orderError && (
            <p style={{ marginBottom: 0 }} data-testid={`release-${release.channel}-preview`}>
              <ImpactPreview before={before} after={after} />
            </p>
          )}

          {notice && (
            <div
              className={
                notice.tone === 'success' ? 'info-banner' : notice.tone === 'warning' ? 'warning-banner' : 'error-banner'
              }
              role={notice.tone === 'success' ? 'status' : 'alert'}
              style={{ marginTop: 12 }}
            >
              {notice.text}
            </div>
          )}

          <div className="btn-row" style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-sm"
              disabled={!feedOk || !dirty || !!orderError || saving}
              onClick={() => setConfirming(true)}
            >
              Salvar versões {channelOf(release.channel)}
            </button>
            {dirty && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={saving}
                onClick={() => {
                  setDraft(null);
                  setNotice(null);
                }}
              >
                Descartar escolha
              </button>
            )}
          </div>
        </div>
      )}

      {confirming && (
        <SimpleConfirmDialog
          title={`Salvar versões ${channelOf(release.channel)}?`}
          confirmLabel="Salvar"
          submitting={saving}
          onCancel={() => setConfirming(false)}
          onConfirm={save}
          body={
            <>
              <p style={{ marginTop: 0 }}>
                Mínima: <PolicyChange from={current.minimumVersion} to={choice.minimumVersion} empty="sem mínima" />
                <br />
                Recomendada:{' '}
                <PolicyChange from={current.recommendedVersion} to={choice.recommendedVersion} empty="sem recomendada" />
              </p>
              <p>
                <ImpactPreview before={before} after={after} />
              </p>
              <p style={{ marginBottom: 0, fontSize: 13 }}>
                Vale na hora para o painel das lojas do canal e para os pedidos de atualização
                confirmados depois. Pedido já aberto mantém a versão-alvo.
              </p>
            </>
          }
        />
      )}
    </div>
  );
}

function PolicyChange({ from, to, empty }: { from: string | null; to: string | null; empty: string }) {
  if (from === to) return <>{to ?? empty} (sem mudança)</>;
  return (
    <>
      {from ?? empty} → <strong>{to ?? empty}</strong>
    </>
  );
}

/** A prévia do impacto: o que passa a valer, com o número de hoje ao lado. */
function ImpactPreview({ before, after }: { before: LevelCounts; after: LevelCounts }) {
  return (
    <>
      Com esta escolha, <strong>{computers(after.mandatory)}</strong> do canal{' '}
      {after.mandatory === 1 ? 'passa' : 'passam'} a ver o diálogo
      obrigatório de atualização (hoje {intFmt.format(before.mandatory)}) e{' '}
      <strong>{computers(after.recommended)}</strong> o aviso de atualização recomendada (hoje{' '}
      {intFmt.format(before.recommended)}).
      {after.unknown > 0 && ` ${computers(after.unknown)} sem versão conhecida ficam sem aviso.`}
    </>
  );
}

function FeedBadge({ release }: { release: AgentReleaseChannel }) {
  switch (release.feed.state) {
    case 'ok':
      return (
        <Badge tone="success" dot>
          Feed lido{release.feed.readAt ? ` em ${formatWhen(release.feed.readAt)}` : ''}
        </Badge>
      );
    case 'unavailable':
      return (
        <Badge tone="warning" dot>
          Feed indisponível
        </Badge>
      );
    case 'not_configured':
      return <Badge tone="neutral">Sem feed</Badge>;
  }
}

function VersionsTable({ release, options }: { release: AgentReleaseChannel; options: Set<string> }) {
  const label = channelLabel(release.channel);
  if (release.versions.length === 0) {
    return (
      <p className="muted" style={{ margin: '0 0 12px' }} data-testid={`release-${release.channel}-no-versions`}>
        {noVersionsText(release)}
      </p>
    );
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <div className="card-subtitle" style={{ marginBottom: 6 }}>
        Versões publicadas
      </div>
      <CardTable aria-label={`Versões publicadas no ${label.toLowerCase()}`}>
        <THead>
          <Tr>
            <Th>Versão</Th>
            <Th>Publicada em</Th>
            <Th>Assinatura</Th>
            <Th>Na política</Th>
          </Tr>
        </THead>
        <TBody>
          {release.versions.map((v) => (
            <Tr key={v.version}>
              <Td heading>
                <span className="mono">{v.version}</span>
              </Td>
              <Td label="Publicada em">{formatWhen(v.publishedAt)}</Td>
              <Td label="Assinatura">
                {v.signed ? (
                  <span className="muted">assinada</span>
                ) : (
                  <Badge tone="warning">sem assinatura</Badge>
                )}
                {!options.has(v.version) && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    fora das opções do canal
                  </div>
                )}
              </Td>
              <Td label="Na política">
                {v.version === release.minimumVersion && <Badge tone="error">mínima</Badge>}{' '}
                {v.version === release.recommendedVersion && <Badge tone="info">recomendada</Badge>}
                {v.version !== release.minimumVersion && v.version !== release.recommendedVersion && (
                  <span className="muted">—</span>
                )}
              </Td>
            </Tr>
          ))}
        </TBody>
      </CardTable>
    </div>
  );
}

/**
 * O canal sem versão para listar. Com o feed indisponível e nenhuma leitura boa antes, a tela
 * não sabe se há versão publicada, e não afirma que não há. Com o feed lido, o estável vazio é
 * o esperado até a primeira promoção: a API lê o manifesto ausente (404) como canal sem versão.
 */
function noVersionsText(release: AgentReleaseChannel): string {
  if (release.feed.state === 'unavailable') return 'Nenhuma versão lida do canal ainda.';
  if (release.feed.state === 'ok' && release.channel === 'stable') {
    return 'Nenhuma versão publicada no canal. O estável fica vazio até a primeira promoção de uma versão do piloto.';
  }
  return 'Nenhuma versão publicada no canal.';
}

function FleetLists({ release, minimumProtocol }: { release: AgentReleaseChannel; minimumProtocol: number }) {
  const label = channelLabel(release.channel).toLowerCase();
  if (release.agentVersions.length === 0 && release.protocolVersions.length === 0) {
    return (
      <p className="muted" style={{ margin: 0 }}>
        Nenhum computador ativo nas lojas do canal.
      </p>
    );
  }

  return (
    <div className="grid-halves">
      <div>
        <div className="muted" style={{ marginBottom: 6 }}>
          Computadores por versão
        </div>
        <ul aria-label={`Computadores do ${label} por versão`} style={{ margin: 0, paddingLeft: 18 }}>
          {release.agentVersions.map((v) => (
            <li key={v.version ?? 'desconhecida'}>
              <span className="mono">{v.version ?? 'desconhecida'}</span> — {computers(v.agents)}
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="muted" style={{ marginBottom: 6 }}>
          Computadores por protocolo
        </div>
        <ul aria-label={`Computadores do ${label} por protocolo`} style={{ margin: 0, paddingLeft: 18 }}>
          {release.protocolVersions.map((p) => (
            <li key={p.protocolVersion ?? 'desconhecido'}>
              protocolo <span className="mono">{p.protocolVersion ?? 'desconhecido'}</span> — {computers(p.agents)}
              {p.protocolVersion !== null && p.protocolVersion < minimumProtocol && (
                <>
                  {' '}
                  <Badge tone="error">abaixo da mínima</Badge>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
